import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Plus, X, AlertCircle } from 'lucide-react';

export default function AdminConfigPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/config')
      .then(r => setConfig(r.data))
      .catch(() => navigate('/admin/login'));
  }, [navigate]);

  const saveConfig = async () => {
    // Validate share templates
    if (!config.whatsappShareTemplate?.includes('{CHALLENGE_URL}')) {
      toast.error('WhatsApp Template muss {CHALLENGE_URL} enthalten');
      return;
    }
    if (!config.linkedinShareTemplate?.includes('{CHALLENGE_URL}')) {
      toast.error('LinkedIn Template muss {CHALLENGE_URL} enthalten');
      return;
    }
    
    setSaving(true);
    try {
      const res = await adminApi.put('/admin/config', config);
      setConfig(res.data);
      toast.success('Konfiguration gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    setSaving(false);
  };

  const updateField = (key, val) => setConfig(c => ({ ...c, [key]: val }));
  const updateNested = (parent, key, val) => setConfig(c => ({ ...c, [parent]: { ...c[parent], [key]: val } }));

  const addInterest = (key) => {
    const val = prompt('Neues Interesse:');
    if (val) setConfig(c => ({ ...c, [key]: [...(c[key] || []), val] }));
  };
  const removeInterest = (key, idx) => {
    setConfig(c => ({ ...c, [key]: c[key].filter((_, i) => i !== idx) }));
  };

  if (!config) return <AdminLayout><div className="text-[#9CA3AF] animate-pulse">Lade Konfiguration...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-config-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-outfit font-bold text-2xl text-white">Konfiguration</h1>
          <Button onClick={saveConfig} disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-config-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quiz Settings */}
          <div className="bg-[#121214] border border-white/5 rounded-xl p-6">
            <h2 className="font-outfit font-bold text-lg text-white mb-4">Quiz-Einstellungen</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-[#9CA3AF] text-xs">Zeit pro Frage (Sekunden)</Label>
                <Input type="number" value={config.timePerQuestionSeconds} onChange={e => updateField('timePerQuestionSeconds', parseInt(e.target.value) || 20)}
                  className="bg-white/5 border-white/10 text-white mt-1 w-24" data-testid="config-time" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                {['anfaenger', 'fortgeschritten', 'experte'].map(lvl => (
                  <div key={lvl}>
                    <Label className="text-[#9CA3AF] text-xs capitalize">{lvl === 'anfaenger' ? 'Anfänger' : lvl === 'fortgeschritten' ? 'Fortgeschritten' : 'Experte'} Fragen</Label>
                    <Input type="number" value={config.questionsPerRun?.[lvl] || ''} onChange={e => updateNested('questionsPerRun', lvl, parseInt(e.target.value) || 15)}
                      className="bg-white/5 border-white/10 text-white mt-1" data-testid={`config-q-${lvl}`} />
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">Freischalt-Schwelle (%)</Label>
                <Input type="number" value={config.unlockThresholdPercent} onChange={e => updateField('unlockThresholdPercent', parseInt(e.target.value) || 75)}
                  className="bg-white/5 border-white/10 text-white mt-1 w-24" data-testid="config-threshold" />
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">Rundungsregel</Label>
                <Select value={config.roundingRule} onValueChange={v => updateField('roundingRule', v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1 w-40" data-testid="config-rounding"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10">
                    <SelectItem value="ceil">Aufrunden (Ceil)</SelectItem>
                    <SelectItem value="floor">Abrunden (Floor)</SelectItem>
                    <SelectItem value="nearest">Runden (Nearest)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">Challenge Ablauf (Tage)</Label>
                <Input type="number" value={config.challengeExpiryDays} onChange={e => updateField('challengeExpiryDays', parseInt(e.target.value) || 14)}
                  className="bg-white/5 border-white/10 text-white mt-1 w-24" data-testid="config-expiry" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-[#9CA3AF] text-xs">Feedback pro Frage</Label>
                <Switch checked={config.showPerQuestionFeedback} onCheckedChange={v => updateField('showPerQuestionFeedback', v)} data-testid="config-feedback-toggle" />
              </div>
            </div>
          </div>

          {/* Share Settings */}
          <div className="bg-[#121214] border border-white/5 rounded-xl p-6">
            <h2 className="font-outfit font-bold text-lg text-white mb-4">Challenge Share-Texte</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-[#3B82F6]/10 border border-[#3B82F6]/20 rounded-lg text-xs text-[#60A5FA]">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Platzhalter: <code className="bg-white/10 px-1 rounded">{'{CHALLENGE_URL}'}</code> (Pflicht), <code className="bg-white/10 px-1 rounded">{'{LEVEL}'}</code> (optional)</span>
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">WhatsApp Share Text</Label>
                <Textarea
                  value={config.whatsappShareTemplate || ''}
                  onChange={e => updateField('whatsappShareTemplate', e.target.value)}
                  placeholder="Challenge angenommen? 😄 Teste dein IT-Wissen hier: {CHALLENGE_URL}"
                  className="bg-white/5 border-white/10 text-white mt-1 h-20 text-sm resize-none"
                  data-testid="config-whatsapp-template"
                />
                {config.whatsappShareTemplate && !config.whatsappShareTemplate.includes('{CHALLENGE_URL}') && (
                  <p className="text-xs text-red-400 mt-1">⚠ Template muss {'{CHALLENGE_URL}'} enthalten</p>
                )}
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">LinkedIn Share Text</Label>
                <Textarea
                  value={config.linkedinShareTemplate || ''}
                  onChange={e => updateField('linkedinShareTemplate', e.target.value)}
                  placeholder="Ich habe ein IT-Quiz gespielt – schaffst du mehr Punkte? {CHALLENGE_URL}"
                  className="bg-white/5 border-white/10 text-white mt-1 h-20 text-sm resize-none"
                  data-testid="config-linkedin-template"
                />
                {config.linkedinShareTemplate && !config.linkedinShareTemplate.includes('{CHALLENGE_URL}') && (
                  <p className="text-xs text-red-400 mt-1">⚠ Template muss {'{CHALLENGE_URL}'} enthalten</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Application Settings */}
          <div className="bg-[#121214] border border-white/5 rounded-xl p-6">
            <h2 className="font-outfit font-bold text-lg text-white mb-4">Bewerbungs-Einstellungen</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-[#9CA3AF] text-xs">PLZ Pflicht</Label>
                <Switch checked={config.postalCodeRequired} onCheckedChange={v => updateField('postalCodeRequired', v)} data-testid="config-postalCode-toggle" />
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">Max Challenge-Nachricht Zeichen</Label>
                <Input type="number" value={config.challengeMessageMaxLength} onChange={e => updateField('challengeMessageMaxLength', parseInt(e.target.value) || 140)}
                  className="bg-white/5 border-white/10 text-white mt-1 w-24" data-testid="config-msg-len" />
              </div>

              {/* Azubi Interests */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[#9CA3AF] text-xs">Azubi-Interessen</Label>
                  <Button variant="ghost" size="sm" onClick={() => addInterest('azubiInterests')} className="text-[#3B82F6] h-6" data-testid="add-azubi-interest">
                    <Plus className="w-3 h-3 mr-1" /> Hinzufügen
                  </Button>
                </div>
                <div className="space-y-1">
                  {config.azubiInterests?.map((item, i) => (
                    <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 text-sm text-white">
                      <span>{item}</span>
                      <button onClick={() => removeInterest('azubiInterests', i)} className="text-[#4B5563] hover:text-red-400"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-[#4B5563]">Multi-Select</span>
                  <Switch checked={config.azubiInterestMultiSelect} onCheckedChange={v => updateField('azubiInterestMultiSelect', v)} />
                </div>
              </div>

              {/* Profi Interests */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[#9CA3AF] text-xs">Profi-Interessen</Label>
                  <Button variant="ghost" size="sm" onClick={() => addInterest('profiInterests')} className="text-[#8B5CF6] h-6" data-testid="add-profi-interest">
                    <Plus className="w-3 h-3 mr-1" /> Hinzufügen
                  </Button>
                </div>
                <div className="space-y-1">
                  {config.profiInterests?.map((item, i) => (
                    <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 text-sm text-white">
                      <span>{item}</span>
                      <button onClick={() => removeInterest('profiInterests', i)} className="text-[#4B5563] hover:text-red-400"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-[#4B5563]">Multi-Select</span>
                  <Switch checked={config.profiInterestMultiSelect} onCheckedChange={v => updateField('profiInterestMultiSelect', v)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
